import { useEffect, useState } from 'react'
import { missingVariants, type Media } from '@nhanho/shared'
import { media as mediaApi, explainError } from '../../lib/api'
import { useToast } from '../../lib/toast'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Spinner } from '../ui/Spinner'
import { UploadDropzone } from './UploadDropzone'

const PAGE_SIZE = 60

/**
 * Chọn ảnh từ thư viện hoặc upload mới. `multiple` → trả về mảng theo thứ tự chọn.
 * Thư viện tải theo trang ("Tải thêm") — trước đây cứng 60 ảnh, ảnh thứ 61 không với tới.
 */
export function MediaPicker({
  open, onClose, onPick, multiple, exclude = [],
}: { open: boolean; onClose: () => void; onPick: (docs: Media[]) => void; multiple?: boolean; exclude?: string[] }) {
  const [items, setItems] = useState<Media[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [picked, setPicked] = useState<Media[]>([])
  const toast = useToast()

  // Mở lại / đổi từ khoá → về trang 1.
  useEffect(() => {
    if (!open) return
    setPicked([])
    setPage(1)
  }, [open, search])

  useEffect(() => {
    if (!open) return
    /**
     * `alive` chặn response cũ đè lên mới (gõ tìm kiếm nhanh, hoặc "Tải thêm" xong đổi từ
     * khoá). Và `setLoading(true)` nằm **trong** timer, không ở ngoài: để ngoài thì lượt
     * effect nào bị huỷ trước khi timer nổ sẽ để lại `loading = true` vĩnh viễn — spinner
     * quay mãi trong khi không có request nào đang chạy.
     */
    let alive = true
    const t = window.setTimeout(() => {
      setLoading(true)
      mediaApi.list({ limit: PAGE_SIZE, page, search: search || undefined })
        .then((r) => {
          if (!alive) return
          setItems((xs) => (page === 1 ? r.docs : [...xs, ...r.docs]))
          setHasMore(r.hasNextPage)
          setTotal(r.totalDocs)
        })
        .catch((e) => alive && toast.error(explainError(e).message))
        .finally(() => alive && setLoading(false))
    }, search ? 250 : 0)
    return () => {
      alive = false
      window.clearTimeout(t)
    }
  }, [open, search, page, toast])

  const toggle = (m: Media) => {
    if (!multiple) { onPick([m]); onClose(); return }
    setPicked((p) => (p.some((x) => x.id === m.id) ? p.filter((x) => x.id !== m.id) : [...p, m]))
  }

  const visible = items.filter((m) => !exclude.includes(m.id))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={multiple ? 'Chọn ảnh' : 'Chọn một ảnh'}
      wide
      footer={
        multiple ? (
          <>
            <Button variant="secondary" onClick={onClose}>Huỷ</Button>
            <Button disabled={!picked.length} onClick={() => { onPick(picked); onClose() }}>Chọn {picked.length || ''}</Button>
          </>
        ) : undefined
      }
    >
      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_320px]">
        <Input placeholder="Tìm theo alt hoặc tên file…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <UploadDropzone compact onUploaded={(docs) => { setItems((xs) => [...docs, ...xs]); setTotal((n) => n + docs.length); if (!multiple) { onPick(docs.slice(0, 1)); onClose() } else setPicked((p) => [...p, ...docs]) }} />
      </div>
      {loading && page === 1 ? (
        <div className="grid place-items-center py-12"><Spinner /></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {visible.map((m) => {
              const sel = picked.some((x) => x.id === m.id)
              const thieu = missingVariants(m.width, m.height)
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m)}
                  className={`group relative aspect-square overflow-hidden rounded-image border-2 bg-sand ${sel ? 'border-primary-700' : 'border-transparent hover:border-primary-300'}`}
                  title={m.alt}
                >
                  <img src={m.sizes?.thumbnail?.url ?? m.url} alt={m.alt} className="size-full object-cover" loading="lazy" />
                  {/*
                    Số đo ảnh GỐC, không phải của thumbnail đang hiện — đó mới là con số quyết
                    định biến thể nào sinh được. `thieu` không rỗng nghĩa là ảnh nhỏ hơn mục
                    tiêu nào đó, api sẽ bỏ qua biến thể ấy và web rơi về ảnh gốc.
                  */}
                  <span className={`pointer-events-none absolute inset-x-0 bottom-0 px-1.5 py-0.5 text-xs text-white ${thieu.length > 0 ? 'bg-warning/85' : 'bg-ink/65'}`}>
                    {m.width}×{m.height}
                    {thieu.length > 0 && <> · thiếu {thieu.join(', ')}</>}
                  </span>
                  {sel && <span className="absolute right-1.5 top-1.5 grid size-5 place-items-center rounded-full bg-primary-700 text-xs text-white">✓</span>}
                </button>
              )
            })}
            {!items.length && <p className="col-span-full py-8 text-center text-sm text-ink-muted">Thư viện trống — tải ảnh lên ở khung bên trên.</p>}
            {items.length > 0 && !visible.length && <p className="col-span-full py-8 text-center text-sm text-ink-muted">Mọi ảnh ở trang này đã được chọn — tải thêm hoặc tìm ảnh khác.</p>}
          </div>
          <div className="mt-4 flex items-center justify-between text-xs text-ink-muted">
            <span>{items.length}/{total} ảnh</span>
            {hasMore && <Button variant="secondary" size="sm" loading={loading} onClick={() => setPage((p) => p + 1)}>Tải thêm</Button>}
          </div>
        </>
      )}
    </Modal>
  )
}
